import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getComparator,
  stableSort,
} from "../../../../common-utilities/sortingUtils";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import { GetEnviroTestKits,DeleteEnviroTestKit } from "../../../services/EnviroTestKitsService";
import ViewEnviroTestKit from "./ViewEnviroTestKit";
import CommonLoader from "../../common/CommonLoader";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddEnviroTestKit from "./AddEnviroTestKit";

const EnviroTestKitsTable = () => {
  const columns = [
    "Id",
    "TypeForm Name",
    "Kit Type (internal use)",
    "Label Name",
    "Number Of Samples", 
    "Priority",
    "TAT",
    "In Order Generator",
    "Actions",
  ];
  const columnHeaders = [
    "sampleKitId",
    "vlpName",
    "nameFromSite",
    "shortLabelName",
    "numberOfSamples",
    "priority",
    "turnAroundTime",
  ];
  // Sort key per column; null means the column is not sortable.
  const sortKeys = [...columnHeaders, "showInOrderGenerator", null];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  // stickyHeader gives head cells their own background, which would hide the
  // blue TableHead, so the color and the white sort labels are set per cell.
  const headCellSx = {
    backgroundColor: (theme) => theme.palette.primary.main,
    color: "white",
    "& .MuiTableSortLabel-root": { color: "white" },
    "& .MuiTableSortLabel-root:hover": { color: "white" },
    "& .MuiTableSortLabel-root.Mui-active": { color: "white" },
    "& .MuiTableSortLabel-icon": { color: "white !important" },
  };

  const [kitsData, setKitsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKit, setSelectedKit] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isAddKitDialogOpen, setIsAddKitDialogOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  const tableContainerRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(460);

  useEffect(() => {
    const fetchKits = async () => {
      try {
        setLoading(true);
        const response = await GetEnviroTestKits();
        setKitsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching kits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKits();
  }, []);

  useEffect(() => {
    const fetchKits = async () => {
      try {
        setLoading(true);
        const response = await GetEnviroTestKits();
        setKitsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching kits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKits();
  }, [isDeleted]);

  useEffect(() => {
    if (!kitsData) {
      return;
    }
  
    const filteredResults = kitsData.filter((kit) =>
      columnHeaders.some((header) => {
        const value = kit[header];
        return value && typeof value === "string"
          ? value.toLowerCase().includes(searchTerm.toLowerCase())
          : false;
      })
    );
  
    setFilteredData(filteredResults);
  }, [searchTerm, kitsData]);

  useEffect(() => {
    const calculateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerTop =
          tableContainerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const availableHeight = windowHeight - containerTop - 76;
        const calculatedHeight = Math.max(
          300,
          Math.min(availableHeight, windowHeight - 200)
        );
        setTableHeight(calculatedHeight);
      }
    };

    calculateTableHeight();
    window.addEventListener("resize", calculateTableHeight);
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener("resize", calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [filteredData, selectedKit]); // re-runs when data changes or the view swaps

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (kit) => {
    setSelectedKit(kit);
  };

  const handleBackClick = () => {
    setSelectedKit(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (kit) => {
    setSelectedKit(kit.sampleKitId);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedKit(null);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
        setLoading(true);
      const response = await DeleteEnviroTestKit(selectedKit);
      if (response.results === true) {
        NotificationManager.success("Test kit deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setIsDeleted(!isDeleted);
        setSelectedKit(null);
      } else {
        NotificationManager.error("Error deleting test kit");
      }
    } catch (error) {
      NotificationManager.error("Error deleting test kit");
    }
    finally {
        setLoading(false);
      }
  };

  const handleAddKitClick = () => {
    setSelectedKit(null);
    setIsAddKitDialogOpen(true);
  };

  const handleEditKitClick = (kit) => {
    setSelectedKit(kit);
    setIsAddKitDialogOpen(true);
  };

  const handleAddKitDialogClose = () => {
    setIsAddKitDialogOpen(false);
    setSelectedKit(null);
  };

  const handleAddKitDialogSuccess = () => {
    setIsAddKitDialogOpen(false);
    setSelectedKit(null);
    setIsDeleted(!isDeleted);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0); // sorting is client side, so keep the user on the first page
  };

  const sortedData = stableSort(filteredData, getComparator(order, orderBy));

  const renderActions = (kit) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditKitClick(kit)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(kit)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(kit)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <span className="title">Enviro Test Kits</span>
      <hr />
      {selectedKit && !isAddKitDialogOpen && !isDeleteConfirmationOpen ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewEnviroTestKit kit={selectedKit} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddKitClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <Box sx={{ overflowX: "auto" }}>
              <TableContainer
                ref={tableContainerRef}
                sx={{ maxHeight: tableHeight, overflowY: "auto" }}
              >
                <Table sx={{ minWidth: 400 }} size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((column, index) => (
                        <TableCell key={column} sx={headCellSx}>
                          {sortKeys[index] ? (
                            <TableSortLabel
                              active={orderBy === sortKeys[index]}
                              direction={
                                orderBy === sortKeys[index] ? order : "asc"
                              }
                              onClick={() => handleRequestSort(sortKeys[index])}
                            >
                              {column}
                            </TableSortLabel>
                          ) : (
                            column
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((kit, index) => (
                        <TableRow
                          key={kit.sampleKitId}
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                          }}
                        >
                          {columnHeaders.map((column) => (
                            <TableCell key={column}>{kit[column]}</TableCell>
                          ))}
                          <TableCell>
                            {kit.showInOrderGenerator !== false ? "Yes" : "No"}
                          </TableCell>
                          <TableCell>{renderActions(kit)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation"
            message="Are you sure you want to delete this kit?"
            color="red"
          />
        </>
      )}

      {isAddKitDialogOpen && (
        <AddEnviroTestKit
          open={isAddKitDialogOpen}
          onClose={handleAddKitDialogClose}
          onSave={handleAddKitDialogSuccess}
          selectedKit={selectedKit}
        />
      )}
    </>
  );
};

export default EnviroTestKitsTable;
